import { combineReducers, createReducer } from '@reduxjs/toolkit';
import { Nullable, RequestState } from 'modules/types/common';
import { switchAccountSuccess } from 'store/account/actions';
import { MedicalHistoryData, ShareAllergies } from 'store/medicalHistory/types';
import { oc } from 'ts-optchain';
import * as actions from './actions';

export interface AllergiesListState {
  loading: boolean;
  error: null | Error;
  data: MedicalHistoryData[];
  lastUpdated: string | undefined;
  requestState: RequestState;
}

export interface ShareAllergiesState {
  isSendingAllergies: boolean;
  sharedAllergiesData: ShareAllergies | {};
  sharedAllergiesError: Nullable<Error>;
}

export interface AllergiesDownloadsState {
  isDownloading: boolean;
  error: Nullable<Error>;
}

export interface AllergiesState {
  allergies: AllergiesListState;
  shareAllergies: ShareAllergiesState;
  downloadAllergies: AllergiesDownloadsState;
}

export const allergiesInitialState = (): AllergiesListState => ({
  loading: false,
  data: [],
  error: null,
  lastUpdated: undefined,
  requestState: RequestState.INITIALIZED
});

const allergiesReducer = createReducer(allergiesInitialState(), {
  [actions.GET_ALLERGIES]: state => {
    state.loading = true;
    state.error = null;
    state.requestState = RequestState.REQUESTING;
  },
  [actions.GET_ALLERGIES_FAIL]: (state, action) => {
    state.loading = false;
    state.error = action.error;
    state.requestState = RequestState.ERROR;
  },
  [actions.GET_ALLERGIES_SUCCESS]: (state, action) => {
    state.loading = false;
    state.data = oc(action).payload.data([]);
    state.requestState = RequestState.SUCCESS;
  },
  [actions.GET_ALLERGIES_CACHE_SUCCESS]: (state, action) => {
    state.data = action.payload.data;
    state.lastUpdated = action.payload.cacheUpdatedAt;
  },
  [switchAccountSuccess.type]: () => allergiesInitialState()
});

export const shareAllergiesInitialState = (): ShareAllergiesState => ({
  isSendingAllergies: false,
  sharedAllergiesData: {},
  sharedAllergiesError: null
});

const shareAllergiesReducer = createReducer(shareAllergiesInitialState(), {
  [actions.SHARE_ALLERGIES]: state => {
    state.isSendingAllergies = true;
    state.sharedAllergiesError = null;
  },
  [actions.SHARE_ALLERGIES_SUCCESS]: (state, action) => {
    state.isSendingAllergies = false;
    state.sharedAllergiesData = action.payload.data;
  },
  [actions.SHARE_ALLERGIES_FAIL]: (state, action) => {
    state.isSendingAllergies = false;
    state.sharedAllergiesError = action.error;
  }
});

export const allergiesDownloadsInitialState = (): AllergiesDownloadsState => ({
  isDownloading: false,
  error: null
});

const allergiesDownloadsReducer = createReducer(allergiesDownloadsInitialState(), {
  [actions.DOWNLOAD_ALLERGIES]: state => {
    state.isDownloading = true;
    state.error = null;
  },
  [actions.DOWNLOAD_ALLERGIES_SUCCESS]: state => {
    state.isDownloading = false;
  },
  [actions.DOWNLOAD_ALLERGIES_FAIL]: (state, action) => {
    state.isDownloading = false;
    state.error = action.error;
  }
});

export default combineReducers({
  allergies: allergiesReducer,
  shareAllergies: shareAllergiesReducer,
  downloadAllergies: allergiesDownloadsReducer
});
